import React, { useState, useRef, useEffect } from 'react';
import { Button, Badge } from 'src/common';
import { X, Settings, Play, Pause, SkipBack, SkipForward, Share, Calendar, Filter, MoreHorizontal, Maximize2, Info } from 'lucide-react';
import { Event } from '../types/chat';


interface VideoPlayerProps {
    isOpen: boolean;
    initialVideoId?: string;
    allVideos: Event[];
    onClose: () => void;
    soundEnabled?: boolean;
}

export function VideoPlayer({ isOpen, initialVideoId, allVideos, onClose, soundEnabled = true }: VideoPlayerProps) {
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
    const videoRef = useRef<HTMLVideoElement>(null);

    useEffect(() => {
        if (initialVideoId && allVideos.length > 0) {
            const index = allVideos.findIndex(v => v.id === initialVideoId);
            setCurrentVideoIndex(index >= 0 ? index : 0);
        }
    }, [initialVideoId, allVideos]);

    const currentVideo = allVideos[currentVideoIndex];

    if (!isOpen || !currentVideo) return null;

    const togglePlayPause = () => {
        if (videoRef.current) {
            if (isPlaying) {
                videoRef.current.pause();
                setIsPlaying(false);
            } else {
                videoRef.current.play()
                    .then(() => {
                        setIsPlaying(true);
                    })
                    .catch((error) => {
                        console.error('Failed to play video:', error);
                        setIsPlaying(false);
                    });
            }
        }
    };

    const navigateVideo = (direction: 'prev' | 'next') => {
        if (allVideos.length === 0) return;

        let newIndex = currentVideoIndex;
        if (direction === 'prev') {
            newIndex = currentVideoIndex > 0 ? currentVideoIndex - 1 : allVideos.length - 1;
        } else {
            newIndex = currentVideoIndex < allVideos.length - 1 ? currentVideoIndex + 1 : 0;
        }

        setCurrentVideoIndex(newIndex);
        setIsPlaying(false);
        if (videoRef.current) {
            videoRef.current.pause();
            videoRef.current.currentTime = 0;
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-background flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-4 bg-background/80 backdrop-blur-sm">
                <Button
                    variant="ghost"
                    size="icon"
                    onClick={onClose}
                    className="text-foreground hover:bg-accent"
                >
                    <X className="w-6 h-6" />
                </Button>
            </div>

            {/* Video Player */}
            <div className="flex-1 relative flex items-center justify-center p-4">
                {currentVideo && (
                    <>
                        <video
                            key={currentVideoIndex}
                            ref={videoRef}
                            className="max-w-2xl max-h-96 w-auto h-auto object-contain"
                            onClick={togglePlayPause}
                            muted={!soundEnabled}
                            onPlay={() => setIsPlaying(true)}
                            onPause={() => setIsPlaying(false)}
                            onEnded={() => setIsPlaying(false)}
                        >
                            {/* Using a sample video URL - in production, this would be the actual video file */}
                            <source src={currentVideo.videoUrl} type="video/mp4" />
                        </video>

                        {/* Fullscreen Button */}
                        <Button
                            variant="ghost"
                            size="icon"
                            className="absolute bottom-4 right-4 text-foreground hover:bg-accent bg-background/30"
                        >
                            <Maximize2 className="w-6 h-6" />
                        </Button>

                        {/* Play/Pause Overlay */}
                        {!isPlaying && (
                            <div className="absolute inset-0 flex items-center justify-center cursor-pointer" onClick={togglePlayPause}>
                                <div className="w-20 h-20 rounded-full bg-white/90 flex items-center justify-center">
                                    <Play className="w-10 h-10 text-gray-900 ml-1" />
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Bottom Section */}
            <div className="bg-background p-4 space-y-4">
                {/* Description */}
                <div className="flex items-start gap-3 bg-card/50 rounded-lg p-3 hidden">
                    <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                        <span className="text-foreground text-xs">VQA</span>
                    </div>
                    <div className="flex-1">
                        <p className="text-foreground text-sm">
                            {currentVideo?.description || 'No description available.'}{' '}
                            <span className="text-blue-400 light:text-blue-600">Details</span>
                        </p>
                    </div>
                </div>

                {/* Timestamp */}
                <div className="text-center">
                    <p className="text-foreground">{currentVideo?.timestamp?.toLocaleString()}</p>
                </div>

                {/* Playback Controls */}
                <div className="flex items-center justify-center gap-6 py-4">
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => navigateVideo('prev')}
                        className="text-foreground hover:bg-accent w-12 h-12"
                    >
                        <SkipBack className="w-6 h-6" />
                    </Button>
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={togglePlayPause}
                        className="text-foreground hover:bg-accent w-12 h-12"
                    >
                        {isPlaying ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6 ml-0.5" />}
                    </Button>
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => navigateVideo('next')}
                        className="text-foreground hover:bg-accent w-12 h-12"
                    >
                        <SkipForward className="w-6 h-6" />
                    </Button>
                </div>
            </div>
        </div>
    );
}
